import React, { FC, useContext, useEffect, useState } from "react";
import DatePicker, {
  CalendarContainer,
  CalendarContainerProps,
  ReactDatePicker,
} from "react-datepicker";
import { addBusinessDays, isWeekend, format } from "date-fns";
import { LocalizationContext } from "v3/context/localization";
import { Alert } from "@pepdirect/v3/ui/Alert";
import { ExternalLink } from "@pepdirect/v3/ui/ExternalLink";
import { SectionHeading } from "../SectionHeading";
import { CalendarIconSvg } from "@pepdirect/v3/ui/icons";
import { getDayFromToday } from "@pepdirect/v3/helpers/date";
import {
  StyledAlertText,
  StyledCalendar,
  StyledChangeDateText,
  StyledContainer,
  StyledDatePicker,
  StyledDeliveryDate,
  StyledMsgWrapper,
  StyledRDD,
  StyledMsgSubtitle,
} from "./index.styled";
import "react-datepicker/dist/react-datepicker.css";

const CONTACT_EMAIL = "ATDorders@pepsico.com";

export const DEFAULT_REQUESTED_DELIVERY_DATE = getDayFromToday(10);

const CustomCalendarContainer = ({
  className,
  children,
}: CalendarContainerProps) => {
  const { localization } = useContext(LocalizationContext);

  const {
    selectDateText = "Select a delivery date",
    needOrderSoonerText = "If you need your order sooner, please contact your Team Sports Manager.",
  } = localization?.checkout?.rdd || {};

  return (
    <StyledCalendar>
      <CalendarContainer className={className} showPopperArrow>
        <>
          <StyledMsgWrapper>
            {selectDateText}
            <StyledMsgSubtitle>{needOrderSoonerText}</StyledMsgSubtitle>
          </StyledMsgWrapper>
          <div style={{ position: "relative" }}>{children}</div>
        </>
      </CalendarContainer>
    </StyledCalendar>
  );
};

interface RequestedDeliveryDateProps {
  maxDeliveryWindowInDays: number;
  requestedDeliveryDate: Date | null;
  setRequestedDeliveryDate: (date: Date) => void;
}

export const RequestedDeliveryDate: FC<RequestedDeliveryDateProps> = ({
  maxDeliveryWindowInDays,
  requestedDeliveryDate,
  setRequestedDeliveryDate,
}) => {
  const { localization } = useContext(LocalizationContext);

  const {
    headingText = "Delivery Date",
    yourRequestedDateText = "Your requested delivery date is",
    // NEED WAY TO FORMAT DATE AND CALENDAR BASED ON LOCALE
    changeDateText = "Change delivery date",
    noteText = "If you have any issues with your order or difficulty scheduling delivery please email",
  } = localization?.checkout?.rdd || {};

  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = React.useRef<ReactDatePicker>(null);

  useEffect(() => {
    if (!requestedDeliveryDate)
      setRequestedDeliveryDate(DEFAULT_REQUESTED_DELIVERY_DATE);
  }, [requestedDeliveryDate, setRequestedDeliveryDate]);

  const isWeekday = (date: Date) => {
    return !isWeekend(date);
  };

  useEffect(() => {
    if (showDatePicker) {
      datePickerRef.current?.setFocus();
    }
  }, [showDatePicker]);

  const getFirstBusinessDayFromToday = (offset: number) => {
    let day = getDayFromToday(offset);
    const isWeekendDay = isWeekend(day);

    if (isWeekendDay) {
      // MOVE TO MONDAY IF IT IS WEEKEND
      day = addBusinessDays(day, 1);
    }
    return day;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    e.key === "Enter" && setShowDatePicker(!showDatePicker);
  };

  const handleDeliveryDate = (
    date: Date | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _event: React.SyntheticEvent<any, Event> | undefined
  ) => {
    if (date && date instanceof Date) setRequestedDeliveryDate(date);
  };

  return (
    <StyledRDD data-testid="requested-delivery">
      <SectionHeading>{headingText}</SectionHeading>

      <StyledContainer>
        <CalendarIconSvg />
        <StyledDeliveryDate show={!showDatePicker}>
          {yourRequestedDateText}{" "}
          <strong>
            {requestedDeliveryDate &&
              format(requestedDeliveryDate, "EEEE, MMMM d")}
          </strong>
          {/* 👇 used to create more space while wrapping seamlessly */}
          <>&nbsp; &nbsp;</>
          <StyledChangeDateText
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onClick={() => setShowDatePicker(true)}
          >
            {changeDateText}
          </StyledChangeDateText>
        </StyledDeliveryDate>

        <StyledDatePicker show={showDatePicker}>
          <DatePicker
            ref={datePickerRef}
            minDate={getFirstBusinessDayFromToday(10)}
            maxDate={getDayFromToday(maxDeliveryWindowInDays)}
            selected={requestedDeliveryDate}
            filterDate={isWeekday}
            onChange={handleDeliveryDate}
            onCalendarClose={() => setShowDatePicker(false)}
            calendarContainer={CustomCalendarContainer}
            monthsShown={2}
            withPortal
          />
        </StyledDatePicker>
      </StyledContainer>

      <Alert>
        <StyledAlertText>
          {noteText}{" "}
          <ExternalLink href={`mailto:${CONTACT_EMAIL}`} underline>
            {CONTACT_EMAIL}
          </ExternalLink>
          {"."}
        </StyledAlertText>
      </Alert>
    </StyledRDD>
  );
};
